/**
 * `decodeText` (W1-07b formats-package; contract formats/info/types.ts `DecodeTextFn`): text manifests (info.yml,
 * info.txt, info.csv) and PEC charts come in whatever encoding the charter's editor used. Phira decodes them with
 * automatic encoding detection (chardetng + encoding_rs, which also sniffs and strips a BOM; P§2.2), so a GBK or
 * Shift_JIS info.txt loads there, and it must load here too.
 *
 * Order (as encoding_rs / chardetng behave):
 *  1. a byte order mark decides: UTF-8 (EF BB BF), UTF-16LE (FF FE), UTF-16BE (FE FF); the BOM is removed;
 *  2. text that is valid UTF-8 is UTF-8;
 *  3. otherwise every legacy candidate that decodes the bytes without an error (GB18030, Shift_JIS, Big5, EUC-KR,
 *     windows-1252) is scored by how plausible its text is (common Han characters of simplified and traditional
 *     Chinese, kana, common Hangul syllables, Latin-1 letters; control characters, private-use characters,
 *     replacement characters and the half-width katakana that misread GBK text produces count against it) and the
 *     best one wins; ties prefer the earlier candidate (GB18030 first: most Phira charts are Chinese).
 */

import type { DecodeTextFn } from './types';

/** Legacy encodings tried after UTF-8, in tie-break order (WHATWG TextDecoder labels). */
export const LEGACY_ENCODINGS = ['gb18030', 'shift_jis', 'big5', 'euc-kr', 'windows-1252'] as const;
export type LegacyEncoding = (typeof LEGACY_ENCODINGS)[number];

/** The most frequent simplified Chinese characters (also most frequent traditional ones that look the same). */
const COMMON_HAN_SIMPLIFIED =
  '的一是不了人我在有他这中大来上国个到说们为子和你地出道也时年得就那要下以生会自着去之过家学对可她里后小么心多天' +
  '而能好都然没日于起还发成事只作当想看文无开手十用主行方又如前所本见经头面公同三已老从动两长知民样现分将外但身些' +
  '与高意进把法此实回二理美点月明其种声全工己话儿者向情部正名定女问力机给等几很业最间新什打便位因重被走电四第门' +
  '相次东政海口使教西再平真听世气信北少关并内加化由却代军产入先山五太水万市眼体别处总才场师书比住员九笑性通目华' +
  '报立马命张活难神数件安表原车白应路期叫死常提感金何更反合放做系计或司利受光王果亲界及今京务制解各任至清物台象' +
  '记边共风战干接它许八特觉望直服毛林题建南度统色字请交爱让认算论百吃义科怎元社术结六功指思非流每青管夫连远资队' +
  '跟带花快条院变联言权往展该领传近留红治决周保达办运武半候七必城父强步完革深区即求品士转量空甚众技轻程告江语英' +
  '基派满式李息写呢识极令黑断线争官视脸称注确改刚千复谁始越树持据像药准云影列观习故落拉找按速福营德友造备号费游' +
  '失呀片纪组装奇刻钱读精球热岁随维态敢够哥板送专谈居细收照单古土布兴济乐尽医局病斗价助温妈底待团座际朝杀穿突易' +
  '若钢曲歌星梦夜雨雪春秋冬夏音乐谱绘作者难度等级歌词';

/** Frequent traditional Chinese characters whose form differs from the simplified one. */
const COMMON_HAN_TRADITIONAL =
  '們這來個說為國學對發會時後經過還現與點長實開關問題電話無應當動體讓認從麼樣聽見親戰麗東車書門馬鳥愛氣頭臉風飛龍' +
  '鐘萬華義業產師廣場進遠運連達邊誰語讀變覺計記許試該議請論識負費資買賣質轉輕農選醫錢陽陰難雲靜響韓顏顯飯館處術' +
  '衛裡條權歡歲歷殺漢灣燈營爭狀獨環畫異盡監眾禮種穩窮筆節範簡紅級終組結給統絕綠線練縣總織繼續羅習聖聲職腦臺興舊' +
  '藝藥號蟲衝規視覽觀謝證講警譯護豐貝貢貨貴貿賀賽趕跡躍軍軟輛輪辦遊過違適鄉釋針鋼錄錯鏡間閱陣陳陸隊階際隨險雖雙' +
  '雞離須頂項順領顧類養驗髮鬥魚鮮點黨齊齒樂譜詞曲夢歡';

/** Frequent Hangul syllables (EUC-KR text decodes into these). */
const COMMON_HANGUL =
  '이다는의에하고을가지로기서한사니리자대일수것게어도라요들아나그면해있없보말할적만같정상시주전부우마생으';

const HAN_SIMPLIFIED: ReadonlySet<number> = codePointSet(COMMON_HAN_SIMPLIFIED);
const HAN_TRADITIONAL: ReadonlySet<number> = codePointSet(COMMON_HAN_TRADITIONAL);
const HANGUL: ReadonlySet<number> = codePointSet(COMMON_HANGUL);

function codePointSet(s: string): Set<number> {
  const out = new Set<number>();
  for (const ch of s) out.add(ch.codePointAt(0) ?? 0);
  return out;
}

/** Characters windows-1252 puts at 0x80-0x9F (typographic punctuation and a few letters). */
const CP1252_SPECIALS: ReadonlySet<number> = new Set([
  0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030, 0x0160, 0x2039, 0x0152, 0x017d, 0x2018,
  0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0x017e, 0x0178,
]);

/** A character from the single-byte Latin range (what windows-1252 produces for bytes >= 0x80). */
function isLatinHigh(cp: number): boolean {
  return (cp >= 0xa0 && cp <= 0xff) || CP1252_SPECIALS.has(cp);
}

/**
 * Plausibility of one decoded character (see the module header). Latin letters with accents are plausible on their
 * own (`Café`), but runs of them are what a CJK text read as windows-1252 looks like (`ÄãºÃ`), so a Latin-range
 * character next to another non-ASCII character counts against the decoding.
 */
function charScore(cp: number, prev: number, next: number): number {
  if (cp === 0x09 || cp === 0x0a || cp === 0x0d) return 0;
  if (cp < 0x20 || cp === 0x7f || (cp >= 0x80 && cp <= 0x9f)) return -10;
  if (cp < 0x7f) return 0;
  if (cp === 0xfffd) return -10;
  if (cp >= 0xe000 && cp <= 0xf8ff) return -8;
  if (isLatinHigh(cp)) {
    if (prev >= 0x80 || next >= 0x80) return -2;
    return cp >= 0xc0 && cp !== 0xd7 && cp !== 0xf7 ? 2 : 0;
  }
  if (HAN_SIMPLIFIED.has(cp) || HAN_TRADITIONAL.has(cp)) return 5;
  if (cp >= 0x4e00 && cp <= 0x9fff) return 1;
  if (cp >= 0x3040 && cp <= 0x30ff) return 4;
  if (cp >= 0x3000 && cp <= 0x303f) return 2;
  if (cp >= 0xff01 && cp <= 0xff5e) return 1;
  if (cp >= 0xff61 && cp <= 0xff9f) return -3;
  if (HANGUL.has(cp)) return 4;
  if (cp >= 0xac00 && cp <= 0xd7a3) return 0;
  if (cp >= 0x3400 && cp <= 0x4dbf) return -3;
  if (cp >= 0xf900 && cp <= 0xfaff) return -3;
  if (cp >= 0x2010 && cp <= 0x206f) return 1;
  return -1;
}

/** Sum of the character scores of a decoded text (higher = more plausible). */
export function textPlausibility(text: string): number {
  const cps: number[] = [];
  for (const ch of text) cps.push(ch.codePointAt(0) ?? 0);
  let score = 0;
  for (let i = 0; i < cps.length; i++) score += charScore(cps[i] ?? 0, cps[i - 1] ?? 0, cps[i + 1] ?? 0);
  return score;
}

function tryDecode(label: string, bytes: Uint8Array): string | null {
  try {
    return new TextDecoder(label, { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    return null;
  }
}

/** Lenient decode (never throws); unknown labels fall back to UTF-8. */
function decodeLenient(label: string, bytes: Uint8Array): string {
  try {
    return new TextDecoder(label, { fatal: false, ignoreBOM: true }).decode(bytes);
  } catch {
    return new TextDecoder('utf-8', { fatal: false, ignoreBOM: true }).decode(bytes);
  }
}

/** The best legacy decoding of bytes that are not valid UTF-8. */
export function guessLegacyEncoding(bytes: Uint8Array): { readonly text: string; readonly encoding: string } {
  let best: { text: string; encoding: string; score: number } | null = null;
  for (const label of LEGACY_ENCODINGS) {
    const text = tryDecode(label, bytes);
    if (text === null) continue;
    const score = textPlausibility(text);
    if (best === null || score > best.score) best = { text, encoding: label, score };
  }
  if (best !== null) return { text: best.text, encoding: best.encoding };
  // windows-1252 maps every byte, so this is only reached on a platform without the legacy decoders.
  return { text: decodeLenient('utf-8', bytes), encoding: 'utf-8' };
}

export const decodeText: DecodeTextFn = (bytes) => {
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return { text: decodeLenient('utf-8', bytes.subarray(3)), encoding: 'utf-8', hadBom: true };
  }
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return { text: decodeLenient('utf-16le', bytes.subarray(2)), encoding: 'utf-16le', hadBom: true };
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return { text: decodeLenient('utf-16be', bytes.subarray(2)), encoding: 'utf-16be', hadBom: true };
  }
  const utf8 = tryDecode('utf-8', bytes);
  if (utf8 !== null) return { text: utf8, encoding: 'utf-8', hadBom: false };
  const guess = guessLegacyEncoding(bytes);
  return { text: guess.text, encoding: guess.encoding, hadBom: false };
};
